import { Day, type RenderDate } from "./day";
import {
  getDayOfWeek,
  isSameDay,
  CalendarDate,
  getLocalTimeZone,
} from "@internationalized/date";
import { useMemo } from "react";
import { DaysOfWeek } from "./days-of-week";

export type MonthProperties = {
  highlightRange?: boolean;
  month: CalendarDate;
  rangeStart?: CalendarDate;
  rangeEnd?: CalendarDate;
  startDate: CalendarDate;
  endDate: CalendarDate;
  onDateClick?: ((date: CalendarDate) => void) | undefined;
  renderDate?: RenderDate;
};

const monthFormatter = new Intl.DateTimeFormat("en-us", {
  month: "long",
  year: "numeric",
});

const MonthTitle = ({ children: month }: { children: CalendarDate }) => (
  <div className="py-4 text-center text-body-extra-bold text-core-blue">
    {monthFormatter.format(month.toDate(getLocalTimeZone()))}
  </div>
);

const MonthPadding = ({
  month,
  renderDate,
}: {
  month: CalendarDate;
  renderDate?: RenderDate;
}) =>
  Array.from({ length: getDayOfWeek(month, "en-us") }).map((_, index) => (
    <Day
      highlightRange={false}
      isPadding
      key={`empty-${index.toString()}`}
      month={month}
      renderDate={renderDate}
    />
  ));

const getDaysInMonth = (monthDate: CalendarDate) => {
  const days: CalendarDate[] = [];
  let cursor = new CalendarDate(monthDate.year, monthDate.month, 1);
  const nextMonth = cursor.add({ months: 1 });

  while (cursor.compare(nextMonth) < 0) {
    days.push(cursor);
    cursor = cursor.add({ days: 1 });
  }
  return days;
};

export const Month = ({
  highlightRange = false,
  month,
  rangeStart,
  rangeEnd,
  startDate,
  endDate,
  onDateClick,
  renderDate,
}: MonthProperties) => {
  const days = useMemo(() => getDaysInMonth(month), [month]);

  return (
    <div className="min-w-80">
      <MonthTitle>{month}</MonthTitle>
      <DaysOfWeek className="mx-8 hidden md:grid" />
      <div className="mx-4 mt-4 mb-8 grid grid-cols-7 md:mx-8">
        <MonthPadding month={month} renderDate={renderDate} />
        {days.map((day) => {
          const isDisabled =
            day.compare(startDate) < 0 || day.compare(endDate) > 0;
          const isRangeStart =
            rangeStart !== undefined && isSameDay(day, rangeStart);
          const isRangeEnd = rangeEnd !== undefined && isSameDay(day, rangeEnd);
          const isActive = !isDisabled && (isRangeStart || isRangeEnd);
          const isInRange =
            !isDisabled &&
            !isActive &&
            rangeStart !== undefined &&
            day.compare(rangeStart) > 0 &&
            rangeEnd !== undefined &&
            day.compare(rangeEnd) < 0;
          const handleClick = onDateClick
            ? () => {
                onDateClick(day);
              }
            : undefined;

          return (
            <Day
              date={day}
              highlightRange={highlightRange}
              isActive={isActive}
              isDisabled={isDisabled}
              isInRange={isInRange}
              isRangeEnd={isRangeEnd}
              isRangeStart={isRangeStart}
              key={day.toString()}
              month={month}
              onClick={handleClick}
              rangeEnd={rangeEnd}
              rangeStart={rangeStart}
              renderDate={renderDate}
            />
          );
        })}
      </div>
    </div>
  );
};
Month.displayName = "Calendar.Month";
